import { RefObject } from 'react';
import {
  ConversationRole,
  DeleteMessageRequestArgs,
  Message,
  MessageChunk,
  MessageFooter,
  MessageType,
} from 'src/types';
import { conversationApi } from '../services/conversationApi';
import {
  DEFAULT_CHAT_ID,
  TEMPORARY_EMPTY_MESSAGE_ID,
  TEMPORARY_EMPTY_MESSAGE,
} from 'src/constants';
import {
  recursiveDeepMerge,
  recursiveUpdatePayload,
  getMessageListRefForPDU,
  validateMessageListRef,
  autoscrollToBottom,
} from 'src/utils';
import { VirtuosoMessageListMethods } from '@virtuoso.dev/message-list';

export const addMessageToConversation = ({
  message,
  mainListRef,
  compareListRef,
}: {
  message: Message;
  mainListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const { user_id, conversation_id, message_id, role } = message;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id: conversation_id || DEFAULT_CHAT_ID },
    (draft) => {
      if (!draft) {
        return;
      }

      if (draft.messages?.find((item) => item.message_id === message_id)) {
        return;
      }

      const listRef = getMessageListRefForPDU({
        conversationId: conversation_id,
        mainListRef,
        compareListRef,
      });

      if (role === ConversationRole.USER && listRef) {
        const listLength = listRef.current?.data.get().length;

        listRef.current?.data.append([message, TEMPORARY_EMPTY_MESSAGE], () => {
          return {
            index: listLength || 'LAST',
            align: 'start',
            behavior: 'smooth',
          };
        });
      }

      if (role !== ConversationRole.USER && listRef) {
        listRef.current?.data.batch(() => {
          listRef.current?.data.findAndDelete(
            (item) => item.message_id === TEMPORARY_EMPTY_MESSAGE_ID,
          );
          listRef.current?.data.append([message]);
        });
      }

      if (!draft.messages) {
        draft.messages = [message];
        return;
      }

      draft.messages.push(message);
    },
  );
};

export const replaceConversationChatTemporaryMessageId = ({
  temporaryMessageId,
  message: newMessage,
  mainListRef,
  compareListRef,
}: {
  temporaryMessageId: string;
  message: Message;
  mainListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const { user_id, conversation_id } = newMessage;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id: conversation_id || DEFAULT_CHAT_ID },
    (draft) => {
      const updatedMessageIndex = draft?.messages?.findIndex(
        (item) => item.message_id === temporaryMessageId,
      );

      const listRef = getMessageListRefForPDU({
        conversationId: conversation_id,
        mainListRef,
        compareListRef,
      });

      if (listRef) {
        listRef.current?.data.map((item) =>
          item.message_id === temporaryMessageId ? newMessage : item,
        );
      }

      if (
        updatedMessageIndex &&
        draft?.messages &&
        updatedMessageIndex !== -1
      ) {
        draft.messages[updatedMessageIndex] = newMessage;
      }
    },
  );
};

export const appendConversationMessage = ({
  message,
  mainListRef,
  compareListRef,
}: {
  message: Message;
  mainListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const { user_id, conversation_id, message_id, payload } = message;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id: conversation_id || DEFAULT_CHAT_ID },
    (draft) => {
      const focusMessage = draft?.messages?.find(
        (item: Message) => item.message_id === message_id,
      );

      if (!focusMessage) {
        return;
      }

      const updatedPayload = focusMessage?.payload
        ? recursiveDeepMerge(focusMessage?.payload || {}, payload || {})
        : payload;

      const listRef = getMessageListRefForPDU({
        conversationId: conversation_id,
        mainListRef,
        compareListRef,
      });

      if (listRef) {
        listRef.current?.data.map(
          (item) =>
            item.message_id === message_id
              ? { ...item, ...message, payload: updatedPayload }
              : item,
          autoscrollToBottom(listRef, message_id),
        );
      }

      Object.assign(focusMessage, {
        ...message,
        payload: updatedPayload,
      });
    },
  );
};

export const replaceConversationMessage = ({
  message,
  mainListRef,
  compareListRef,
}: {
  message: Message;
  mainListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const { user_id, conversation_id, message_id } = message;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id: conversation_id || DEFAULT_CHAT_ID },
    (draft) => {
      const focusMessage = draft?.messages?.find(
        (item: Message) => item.message_id === message_id,
      );

      if (!focusMessage) {
        return;
      }

      const listRef = getMessageListRefForPDU({
        conversationId: conversation_id,
        mainListRef,
        compareListRef,
      });

      if (listRef) {
        listRef.current?.data.map(
          (item) =>
            item.message_id === message_id ? { ...item, ...message } : item,
          autoscrollToBottom(listRef, message_id),
        );
      }

      Object.assign(focusMessage, message);
    },
  );
};

export const updateConversationMessage = ({
  message,
  mainListRef,
  compareListRef,
}: {
  message: Message;
  mainListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const { user_id, conversation_id, message_id } = message;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id: conversation_id || DEFAULT_CHAT_ID },
    (draft) => {
      if (!draft) {
        return;
      }

      const listRef = getMessageListRefForPDU({
        conversationId: conversation_id,
        mainListRef,
        compareListRef,
      });

      const focusMessage = draft.messages?.find(
        (item: Message) => item.message_id === message_id,
      );

      if (focusMessage) {
        const updatedPayload = recursiveUpdatePayload(
          focusMessage.payload || {},
          message.payload || {},
        );

        if (listRef) {
          listRef.current?.data.map(
            (item) =>
              item.message_id === message_id
                ? { ...item, ...message, payload: updatedPayload }
                : item,
            autoscrollToBottom(listRef, message_id),
          );
        }

        Object.assign(focusMessage, {
          ...message,
          payload: updatedPayload,
        });
        return;
      }

      if (listRef) {
        listRef.current?.data.batch(() => {
          listRef.current?.data.findAndDelete(
            (item) => item.message_id === TEMPORARY_EMPTY_MESSAGE_ID,
          );
          listRef.current?.data.append([message]);
        });
      }

      draft.messages
        ? draft.messages.push(message)
        : (draft.messages = [message]);
    },
  );
};

// TODO(olha): the same as replaceConversationMessage. double-check and replace
export const addConversationMessageFooter = ({
  message,
  mainListRef,
  compareListRef,
}: {
  message: MessageFooter;
  mainListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const { user_id, conversation_id = DEFAULT_CHAT_ID, message_id } = message;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id },
    (draft) => {
      const focusMessage = draft?.messages?.find(
        (item: Message) => item.message_id === message_id,
      );

      if (!focusMessage) {
        return;
      }

      const listRef = getMessageListRefForPDU({
        conversationId: conversation_id,
        mainListRef,
        compareListRef,
      });

      if (listRef) {
        listRef.current?.data.map(
          (item) =>
            item.message_id === message_id ? { ...item, ...message } : item,
          autoscrollToBottom(listRef, message_id),
        );
      }

      Object.assign(focusMessage, message);
    },
  );
};

export const addConversationMessageContentChunk = ({
  user_id,
  messageChunk,
  mainListRef,
  compareListRef,
}: {
  user_id: string;
  messageChunk: MessageChunk;
  mainListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const {
    conversation_id = DEFAULT_CHAT_ID,
    message_id,
    task_id,
  } = messageChunk;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id },
    (draft) => {
      const focusMessage = draft?.messages?.find(
        (message: Message) => message.message_id === message_id,
      );

      if (!focusMessage) {
        return;
      }

      const newContent = focusMessage.content + messageChunk.content;

      const listRef = getMessageListRefForPDU({
        conversationId: conversation_id,
        mainListRef,
        compareListRef,
      });

      if (listRef) {
        listRef.current?.data.map(
          (item) =>
            item.message_id === message_id
              ? {
                  ...item,
                  task_id,
                  content: newContent,
                }
              : item,
          autoscrollToBottom(listRef, message_id),
        );
      }

      Object.assign(focusMessage, {
        task_id,
        content: newContent,
      });
    },
  );
};

// TODO(olha): the same as addConversationChatMessageContentChunk. double-check and replace
export const addConversationMessageChunk = ({
  user_id,
  messageChunk,
  mainListRef,
  compareListRef,
}: {
  user_id: string;
  messageChunk: MessageChunk;
  mainListRef: RefObject<VirtuosoMessageListMethods<Message>>;
  compareListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const {
    conversation_id = DEFAULT_CHAT_ID,
    message_id,
    task_id,
  } = messageChunk;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id },
    (draft) => {
      const focusMessage = draft?.messages?.find(
        (message: Message) => message.message_id === message_id,
      );

      if (!focusMessage) {
        return;
      }

      const newContent = focusMessage.content + messageChunk.content;

      const listRef = getMessageListRefForPDU({
        conversationId: conversation_id,
        mainListRef,
        compareListRef,
      });

      if (listRef) {
        listRef.current?.data.map(
          (item) =>
            item.message_id === message_id
              ? {
                  ...item,
                  message_type: task_id
                    ? MessageType.TASK_CREATED
                    : MessageType.CONVERSATION,
                  task_id,
                  content: newContent,
                }
              : item,
          autoscrollToBottom(listRef, message_id),
        );
      }

      Object.assign(focusMessage, {
        // (olha): it's a small workaround since if BE sends task_id, the message_type actually was changed
        message_type: task_id
          ? MessageType.TASK_CREATED
          : MessageType.CONVERSATION,
        task_id,
        content: newContent,
      });
    },
  );
};

// this handle can be user only outside useSocket
export const replaceConversationMessageFields = ({
  message,
  messageListRef,
}: {
  message: Message;
  messageListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const { user_id, conversation_id, message_id } = message;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id: conversation_id || DEFAULT_CHAT_ID },
    (draft) => {
      const focusMessage = draft?.messages?.find(
        (item: Message) => item.message_id === message_id,
      );

      if (!focusMessage) {
        return;
      }

      if (
        validateMessageListRef({
          conversationId: conversation_id,
          ref: messageListRef,
        })
      ) {
        messageListRef.current?.data.map((item) =>
          item.message_id === message_id ? { ...item, ...message } : item,
        );
      }

      Object.assign(focusMessage, message);
    },
  );
};

// this handle can be user only outside useSocket
export const overwriteConversationMessage = ({
  message,
  messageListRef,
}: {
  message: Message;
  messageListRef: RefObject<VirtuosoMessageListMethods<Message>>;
}) => {
  const { user_id, conversation_id, message_id } = message;

  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id: conversation_id || DEFAULT_CHAT_ID },
    (draft) => {
      const messageIndex = draft?.messages?.findIndex(
        (item: Message) => item.message_id === message_id,
      );

      if (messageIndex === -1 || messageIndex === undefined) {
        return;
      }

      if (
        validateMessageListRef({
          conversationId: conversation_id,
          ref: messageListRef,
        })
      ) {
        messageListRef.current?.data.map((item) =>
          item.message_id === message_id ? message : item,
        );
      }

      if (draft.messages?.[messageIndex]) {
        draft.messages[messageIndex] = message;
      }
    },
  );
};

export const deleteConversationMessage = ({
  user_id,
  conversation_id,
  message_ids,
  mainMessageListRef,
}: DeleteMessageRequestArgs) => {
  return conversationApi.util.updateQueryData(
    'getConversationById',
    { user_id, conversation_id: conversation_id || DEFAULT_CHAT_ID },
    (draft) => {
      const userMessageId = message_ids[0];
      const agentMessageId = message_ids[1];

      if (!userMessageId || !agentMessageId) {
        return;
      }

      if (mainMessageListRef) {
        mainMessageListRef.current?.data.findAndDelete((item) =>
          item?.message_id ? message_ids.includes(item.message_id) : false,
        );
      }

      if (draft?.messages) {
        draft.messages = draft.messages.filter(
          (item: Message) =>
            item.message_id !== userMessageId &&
            item.message_id !== agentMessageId,
        );
      }
    },
  );
};
